import { forwardRef, useImperativeHandle } from 'react';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { getLocalizedDateTime, getLocalizedString } from '../../localization/LocalizedString';
import '../../views/ServiceReport.css';
import formatSeconds from './TimeFormat';

const sortByDate = (items) => {
  return items.slice().sort((a, b) => new Date(b.totalEndTime) - new Date(a.totalEndTime));
};

const calculateOverallValues = (tasks) => {
  const tasksWithOverallValues = sortByDate([...tasks]);

  let overallDestinationDuration = 0;
  let overallServiceDuration = 0;

  for (const task of tasksWithOverallValues) {
    const serviceDuration = (task.relatedTasks || []).reduce((sum, t) => sum + (t.durationSeconds || 0), 0);
    task.serviceDuration = serviceDuration;
    task.serviceName = (task.service?.[0]?.name) || '';

    overallServiceDuration += serviceDuration;
    overallDestinationDuration += task.totalDuration || 0;
  }

  tasksWithOverallValues.push({
    totalDuration: overallDestinationDuration,
    serviceDuration: overallServiceDuration,
    customer: {},
  });

  return tasksWithOverallValues;
};

const ExportGroupedTaskExcelButton = forwardRef(({ translate, data, currentLanguage }, ref) => {
  const download = async () => {
    const tasksWithOverallValues = calculateOverallValues(data);
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet('Sheet1');

    const overallDurationLabel = `${translate('service_report.column_task.overallDuration')} ${translate('service_report.column_task.timeFormat')}`;
    const workDurationLabel = `${translate('service_report.column_task.workDuration')} ${translate('service_report.column_task.timeFormat')}`;
    const overallDurationSecLabel = `${translate('service_report.column_task.overallDuration')} ${translate('service_report.column_task.secFormat')}`;
    const workDurationSecLabel = `${translate('service_report.column_task.workDuration')} ${translate('service_report.column_task.secFormat')}`;
    const diffLabel = translate('service_report.column_task.diff');

    sheet.addRow([
      translate('service_report.column_task.date'),
      overallDurationLabel,
      workDurationLabel,
      diffLabel,
      overallDurationSecLabel,
      workDurationSecLabel,
      diffLabel,
      translate('service_report.column_task.customer'),
      translate('service_report.column_task.end_customer'),
      translate('service_report.column_task.address'),
      translate('service_report.column_task.city'),
      translate('service_report.column_task.service'),
      translate('service_report.column_task.user'),
    ]);

    tasksWithOverallValues.forEach((col) => {
      sheet.addRow([
        col.totalEndTime ? getLocalizedDateTime(new Date(col.totalEndTime), currentLanguage) : '',
        formatSeconds(col.totalDuration || 0),
        formatSeconds(col.serviceDuration || 0),
        formatSeconds((col.totalDuration || 0) - (col.serviceDuration || 0)),
        col.totalDuration || 0,
        col.serviceDuration || 0,
        (col.totalDuration || 0) - (col.serviceDuration || 0),
        col.customer?.name || '',
        col.companyName || '',
        col.streetAddress || '',
        col.city || '',
        getLocalizedString(col.serviceName || '', currentLanguage),
        col.userId || '',
      ]);
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(blob, `${translate('service_report.search.type_grouped')}.xlsx`);
  };

  useImperativeHandle(ref, () => ({
    download,
  }));

  return null;
});

export default ExportGroupedTaskExcelButton;
